import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import AsyncSelect from 'react-select/async';
import menuPortalProps from 'helpers/reactSelectPortal';
import { createDriver, updateDriver } from 'redux/booking/car/driverSlice';
import { debounce, filterOptions } from 'helpers/debounce';
import { Drawer, Button, FormRow } from 'components/common';

import { INPUT_CLS as inputClass } from 'helpers/formStyles';
const DriverForm = ({ showModal, setshowModal, title, driver }) => {
  const { control, register, handleSubmit, formState: { errors }, setValue, reset } = useForm();
  const dispatch = useDispatch();

  const defaultSiteOption = useMemo(() => (driver ? { value: driver.site_id, label: driver.site_name } : ''), [driver]);
  const defaultCarOption = useMemo(() => (driver ? { value: driver.car_id, label: driver.car_name } : ''), [driver]);

  useEffect(() => { if (title === 'New Driver') reset(); }, [title, reset]);
  useEffect(() => { if (driver) Object.entries(driver).forEach(([key, value]) => setValue(key, value)); }, [setValue, driver]);

  const closeModal = () => setshowModal(false);

  const onSubmit = (data) => {
    try {
      if (driver) dispatch(updateDriver({ driverId: driver.id, data }));
      else dispatch(createDriver(data));
      closeModal();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const debouncedFilterSites = debounce((inputValue, callback) => filterOptions('/organization/sites', inputValue).then(callback), 300);
  const siteOptions = (inputValue, callback) => debouncedFilterSites(inputValue, callback);
  const debouncedFilterCars = debounce((inputValue, callback) => filterOptions('/booking/cars/active_list', inputValue).then(callback), 300);
  const carOptions = (inputValue, callback) => debouncedFilterCars(inputValue, callback);

  return (
    <Drawer
      show={showModal}
      onHide={closeModal}
      title={title}
      footer={(
        <>
          <Button size="sm" variant="secondary" onClick={closeModal}>Cancel</Button>
          <Button size="sm" type="submit" form="driver-form" variant="primary">Save</Button>
        </>
      )}
    >
      <form id="driver-form" onSubmit={handleSubmit(onSubmit)}>
        <FormRow label="Driver name" error={errors.name?.message}>
          <input type="text" placeholder="Driver name" className={inputClass} {...register('name', { required: 'Driver name must be required.' })} />
        </FormRow>
        <FormRow label="Email" error={errors.email?.message}>
          <input type="email" placeholder="example@pebsteel.com.vn" autoComplete="email" className={inputClass}
            {...register('email', { required: 'Email must be required', pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i, message: 'Invalid email address' } })} />
        </FormRow>
        <FormRow label="Phone number" error={errors.phone?.message}>
          <input type="text" placeholder="Phone number" className={inputClass} {...register('phone', { required: 'Phone must be required.' })} />
        </FormRow>
        <FormRow label="Active">
          <Controller control={control} name="active" defaultValue
            render={({ field }) => (
              <label className="tw-inline-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                <input type="checkbox" checked={!!field.value} onChange={(e) => setValue('active', e.target.checked)} />
                <span className="tw-text-sm tw-text-slate-600">Active</span>
              </label>
            )} />
        </FormRow>
        <FormRow label="Car" error={errors.car_id?.message}>
          <Controller control={control} name="car_id" rules={{ required: { value: true, message: 'Car must be required' } }}
            render={() => (
              <AsyncSelect cacheOptions defaultOptions defaultValue={defaultCarOption} loadOptions={carOptions} closeMenuOnSelect placeholder="Select..." classNamePrefix="react-select" {...menuPortalProps}
                onChange={(opt) => setValue('car_id', opt.value)} />
            )} />
        </FormRow>
        <FormRow label="Site" error={errors.site_id?.message}>
          <Controller control={control} name="site_id" rules={{ required: { value: true, message: 'Site must be required' } }}
            render={() => (
              <AsyncSelect cacheOptions defaultOptions defaultValue={defaultSiteOption} loadOptions={siteOptions} closeMenuOnSelect placeholder="Select..." classNamePrefix="react-select" {...menuPortalProps}
                onChange={(opt) => setValue('site_id', opt.value)} />
            )} />
        </FormRow>
      </form>
    </Drawer>
  );
};

DriverForm.propTypes = {
  showModal: PropTypes.bool.isRequired,
  setshowModal: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  driver: PropTypes.object,
};

export default DriverForm;
